/**
 * Product Recommendations
 * This component is used on the PDP pages to display recommended products fetched from Shopify's API.
 * @extends HTMLElement
 */
  class ProductRecommendations extends HTMLElement {

  /** Instantiate a ProductRecommendations Element */
  constructor() {

    /**
     * Inherit HTMLElement methods
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/super
     */
    super();

    /**
     * An intersection observer is required for this async component to properly work in the Shopify Editor
     * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
     */
    new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 200px 0px'}).observe(this);
  }

  /**
   * Handler for Intersection Observer
   * @param {Array} entries An array of IntersectionObserverEntry objects, each representing one threshold which was crossed, either becoming more or less visible than the percentage specified by that threshold.
   * @param {Object} observer The IntersectionObserver for which this callback is being invoked.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserverEntry
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
   */
  async handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    await this.loadRecommendations();
    this.getTaggingData();
    /**
     * GA4 DataLayer Event: View Item List
     * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item_list
     */
    try {
      pushDataLayerEvent({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: 'recommended-products-pdp',
          item_list_name: typeof this.itemListName === 'string' && this.itemListName.length
            ? this.itemListName
            : '',
          items: Array.isArray(this.productData)
            ? this.productData.map(product => {
                const {id: item_id, title: item_name, type: item_category} = product;
                return {
                  item_id,
                  item_name,
                  item_category,
                  currency: this.currencyCode,
                  ...calculatePrice({product})
                };
              })
            : []
        }
      });
    } catch (error) {
      console.log('GA4: view_item_list', error);
    }
  }

  /**
   * Loads product recommendations from Shopify, these must be loaded from an API call.
   * @see https://shopify.dev/api/liquid/objects/recommendations
   */
  async loadRecommendations() {
    // Check if the product recommendations URL is present in the data-url attribute
    if(this.dataset.url !== undefined && this.dataset.url.length) {
      await fetch(this.dataset.url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }
        })
        .catch(e => {
          console.error(e);
        });
    }
  }

  /**
   * Pulls product level data for the recommended products, and the name of the list.
   * @returns {Object} Shopifys recommendations.products object for this product
   * @see https://shopify.dev/docs/api/liquid/objects/recommendations#recommendations-products
   */
  getTaggingData() {
    const jsonProductData = this.querySelector('#json-recommendations[type="application/json"]');
    if(!this.productData && jsonProductData !== null) {
      const {products, item_list_name, currency} = JSON.parse(jsonProductData.textContent);
      this.productData = products;
      this.itemListName = item_list_name;
      this.currencyCode = currency;
      // Remove the <script> tag we used to load the JSON from the DOM
      jsonProductData.parentNode.removeChild(jsonProductData);
    }
    return Array.isArray(this.productData) ? this.productData : [];
  }

}
customElements.define('product-recommendations', ProductRecommendations);
